import {
  KitDestructionDocumentDtoSchema,
  PatientTodoDocumentDtoSchema,
  VclDocumentDtoSchema,
  type KitDestructionDocumentDto,
  type PatientTodoDocumentDto,
  type VclDocumentDto,
} from '@ctms/contracts';

import { apiClient } from '../../api/client.js';
import { API_PATHS } from '../../api/paths.js';

/**
 * Domain 7 client (issue #10 atom 6). Both endpoints are RPC-style POSTs with
 * ONE identifier — the patient visit; everything else is resolved server-side
 * (Q-DOC-001 decision). Neither result is cached: `generate` writes an
 * append-only generation log (REQ-DOC-020) and `renderPdf` produces fresh
 * bytes every call, so both travel through mutations, never queries.
 */
export const documentsApi = {
  /** REQ-DOC-001…016: the composed VCL of one visit as JSON (preview data). */
  async generateVcl(patientVisitId: number): Promise<VclDocumentDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.documents.generateVcl, { json: { patientVisitId } })
      .json();
    return VclDocumentDtoSchema.parse(raw);
  },

  /**
   * REQ-DOC-007/008 (ADR-010): the same VCL rendered to A4 PDF by the worker
   * over the document-pdf queue — sync wait, so the cockpit downloads the
   * bytes directly. HTTP errors (502 `PDF_RENDER_FAILED`, 504
   * `PDF_RENDER_TIMEOUT`) surface as ky `HTTPError` before `.blob()`.
   */
  async renderVclPdf(patientVisitId: number): Promise<Blob> {
    const response = await apiClient.post(API_PATHS.documents.renderVclPdf, {
      json: { patientVisitId },
    });
    return response.blob();
  },

  /**
   * REQ-DOC-018 (issue #31 B1): the composed destruction protocol of ONE D5
   * ledger entry as JSON (preview data) — the same single-id RPC contract as
   * VCL; site, laboratory and person names are resolved server-side.
   */
  async generateKitDestruction(kitOutProtocolId: number): Promise<KitDestructionDocumentDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.documents.generateKitDestruction, { json: { kitOutProtocolId } })
      .json();
    return KitDestructionDocumentDtoSchema.parse(raw);
  },

  /**
   * REQ-DOC-007/018: the same protocol rendered to A4 PDF by the worker over
   * the document-pdf queue — sync wait (SLA < 1,5 s), so the cockpit downloads
   * the bytes directly.
   */
  async renderKitDestructionPdf(kitOutProtocolId: number): Promise<Blob> {
    const response = await apiClient.post(API_PATHS.documents.renderKitDestructionPdf, {
      json: { kitOutProtocolId },
    });
    return response.blob();
  },

  /**
   * REQ-DOC-017 (issue #31 B2): the composed todo-list card of ONE patient
   * visit as JSON (preview data) — the same single-id RPC contract as VCL;
   * patient display data, open tasks and upcoming appointments are resolved
   * server-side. Header carries NO PESEL (the card is handed to the patient).
   */
  async generatePatientTodo(patientVisitId: number): Promise<PatientTodoDocumentDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.documents.generatePatientTodo, { json: { patientVisitId } })
      .json();
    return PatientTodoDocumentDtoSchema.parse(raw);
  },

  /**
   * REQ-DOC-007/017: the same card rendered to A4 PDF by the worker over the
   * document-pdf queue — sync wait (SLA < 1,5 s), so the cockpit downloads the
   * bytes directly.
   */
  async renderPatientTodoPdf(patientVisitId: number): Promise<Blob> {
    const response = await apiClient.post(API_PATHS.documents.renderPatientTodoPdf, {
      json: { patientVisitId },
    });
    return response.blob();
  },
};
